import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api, formatErr, resolveAssetUrl } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Save, ChevronDown, ChevronUp, Eye, EyeOff,
  Youtube, Star, Users, Copy, Bookmark, Upload, ImagePlus, X,
} from "lucide-react";
import { BLOCK_TYPE_LIST } from "../../lib/training";

const uid = () => Math.random().toString(36).slice(2, 11);
const WEEK_DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

// Re-id blocks/exercises so a template can be applied without collisions
const cloneBlocks = (blocks = []) =>
  blocks.map((b) => ({
    ...b,
    id: uid(),
    exercises: (b.exercises || []).map((e) => ({ ...e, id: uid() })),
  }));

/* ---------- Reusable image input: preview + file upload + URL fallback ---------- */
const ImageInput = ({ value, onChange, testId = "image-input", label = "" }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type || !file.type.startsWith("image/")) {
      toast.error("El archivo debe ser una imagen");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Imagen demasiado grande (máx 12 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
      // reset input so the same file can be re-selected
      try { e.target.value = ""; } catch { /* noop */ }
    }
  };
  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative w-full max-w-md">
          <img
            src={resolveAssetUrl(value)}
            alt={label || "preview"}
            className="w-full h-40 object-cover border border-white/10"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid={`${testId}-preview`}
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white p-1.5 rounded"
            aria-label="Quitar imagen"
            data-testid={`${testId}-clear`}
          >
            <X size={14} />
          </button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <label
          className={`qv-btn-ghost text-xs cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          data-testid={`${testId}-upload-label`}
        >
          {uploading ? <Upload size={12} className="animate-pulse" /> : <ImagePlus size={12} />}
          {uploading ? "SUBIENDO..." : (value ? "CAMBIAR IMAGEN" : "SUBIR IMAGEN")}
          <input
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
            data-testid={`${testId}-file`}
          />
        </label>
        <span className="text-[10px] tracking-widest uppercase text-neutral-500">o pega una URL</span>
      </div>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://..."
        className="qv-input"
        data-testid={`${testId}-url`}
      />
    </div>
  );
};

const emptyExercise = () => ({
  id: uid(),
  name: "",
  sets: "",
  reps: "",
  rest: "",
  load: "",
  tempo: "",
  intensity: "",
  notes: "",
  youtube_url: "",
});

const emptyBlock = () => ({ id: uid(), type: "fuerza", name: "", exercises: [emptyExercise()] });

const emptyDay = (dayName = "Lunes") => ({
  id: uid(),
  day: dayName,
  title: "",
  blocks: [emptyBlock()],
  notes: "",
  rest: false,
});

const emptyWeek = (n = 1) => ({
  id: uid(),
  week_number: n,
  title: `Semana ${n}`,
  subtitle: "",
  days: [emptyDay("Lunes"), emptyDay("Miércoles"), emptyDay("Viernes")],
});

/* ---------- Autocomplete for exercise name (uses coaching suggestions endpoint) ---------- */
const ExerciseNameInput = ({ value, onChange, onPick }) => {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    if (!open) return;
    const q = (value || "").trim();
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/exercise-suggestions", { params: { q, limit: 8 } });
        setSuggestions(data || []);
      } catch { setSuggestions([]); }
    }, 180);
    return () => clearTimeout(handle);
  }, [value, open]);

  const pick = (sug) => {
    if (onPick) onPick(sug);
    else onChange(sug.name);
    setOpen(false);
  };

  return (
    <div className="relative">
      <input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        className="qv-input"
        placeholder="Ejercicio (escribe para ver sugerencias)"
        autoComplete="off"
        data-testid="program-exercise-name-input"
      />
      {open && suggestions.length > 0 && (
        <div
          data-testid="program-exercise-suggestions"
          className="absolute z-20 mt-1 w-full bg-[#0a0a0a] border border-white/10 max-h-56 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-0 flex items-center justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-white truncate">{s.name}</div>
                <div className="text-[10px] tracking-widest uppercase text-neutral-500 mt-0.5">
                  {[s.sets && `${s.sets}×${s.reps || "?"}`, s.load, s.intensity, s.youtube_url && "vídeo"].filter(Boolean).join(" · ") || "Sin pauta guardada"}
                </div>
              </div>
              <span className="text-[10px] tracking-widest font-bold text-[var(--qv-red)] flex-shrink-0">×{s.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------- Day editor ---------- */
const DayEditor = ({ day, onChange, onDelete, templates, onSaveTemplate, onDeleteTemplate }) => {
  const [showTpl, setShowTpl] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const update = (patch) => onChange({ ...day, ...patch });
  const updateBlock = (bi, patch) => {
    const blocks = day.blocks.slice();
    blocks[bi] = { ...blocks[bi], ...patch };
    update({ blocks });
  };
  const addBlock = () => update({ blocks: [...day.blocks, emptyBlock()] });
  const delBlock = (bi) => update({ blocks: day.blocks.filter((_, i) => i !== bi) });
  const addEx = (bi) => updateBlock(bi, { exercises: [...day.blocks[bi].exercises, emptyExercise()] });
  const delEx = (bi, ei) => updateBlock(bi, { exercises: day.blocks[bi].exercises.filter((_, i) => i !== ei) });
  const updateEx = (bi, ei, patch) => {
    const exs = day.blocks[bi].exercises.slice();
    exs[ei] = { ...exs[ei], ...patch };
    updateBlock(bi, { exercises: exs });
  };

  const applyTemplate = (tpl) => {
    update({
      title: tpl.title || day.title,
      blocks: cloneBlocks(tpl.blocks || []),
      notes: tpl.notes || day.notes,
    });
    setShowTpl(false);
    toast.success(`Plantilla "${tpl.name}" cargada`);
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Nombre de la plantilla:", day.title || "Mi plantilla");
    if (!name || !name.trim()) return;
    setSavingTpl(true);
    try {
      await onSaveTemplate({
        name: name.trim(),
        day_type: (day.blocks[0] && day.blocks[0].type) || "fuerza",
        title: day.title || null,
        blocks: day.blocks,
        notes: day.notes || null,
      });
    } finally { setSavingTpl(false); }
  };

  return (
    <div className="border border-white/10 bg-[#080808] p-4 space-y-4" data-testid={`day-editor-${day.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={day.day}
          onChange={(e) => update({ day: e.target.value })}
          className="qv-input max-w-[150px]"
        >
          {WEEK_DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <input
          value={day.title}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Ej. Fuerza tren inferior"
          className="qv-input flex-1 min-w-[200px]"
        />
        <label className="text-xs text-neutral-400 inline-flex items-center gap-2 px-3">
          <input type="checkbox" checked={day.rest} onChange={(e) => update({ rest: e.target.checked })} />
          Descanso
        </label>
        <button type="button" onClick={onDelete} className="text-neutral-500 hover:text-red-500 p-2" aria-label="Eliminar día">
          <Trash2 size={16} />
        </button>
      </div>

      {!day.rest && (
        <div className="flex flex-wrap gap-2 relative">
          <button
            type="button"
            onClick={() => setShowTpl((s) => !s)}
            className="qv-btn-ghost text-[10px]"
            data-testid={`load-day-template-${day.id}`}
          >
            <Copy size={12} /> CARGAR PLANTILLA {templates.length > 0 && <span className="text-[var(--qv-red)] ml-1">({templates.length})</span>}
          </button>
          <button
            type="button"
            onClick={saveAsTemplate}
            disabled={savingTpl || day.blocks.length === 0}
            className="qv-btn-ghost text-[10px]"
            style={{ opacity: day.blocks.length === 0 ? 0.5 : 1 }}
            data-testid={`save-day-template-${day.id}`}
            title={day.blocks.length === 0 ? "Añade bloques antes de guardar" : ""}
          >
            <Bookmark size={12} /> {savingTpl ? "GUARDANDO..." : "GUARDAR COMO PLANTILLA"}
          </button>
          {showTpl && (
            <div
              className="absolute z-20 top-full left-0 right-0 mt-2 bg-[#0a0a0a] border border-white/10 max-h-64 overflow-y-auto"
              data-testid={`day-templates-dropdown-${day.id}`}
            >
              {templates.length === 0 ? (
                <div className="p-4 text-xs text-neutral-500 text-center">
                  Aún no tienes plantillas. Configura un día y pulsa &quot;Guardar como plantilla&quot;.
                </div>
              ) : templates.map((tpl) => {
                const totalEx = (tpl.blocks || []).reduce((n, b) => n + (b.exercises || []).length, 0);
                return (
                  <div
                    key={tpl.id}
                    onClick={() => applyTemplate(tpl)}
                    className="px-3 py-2 border-b border-white/5 cursor-pointer hover:bg-white/5 flex items-center justify-between gap-2"
                    data-testid={`day-template-${tpl.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-white truncate">{tpl.name}</div>
                      <div className="text-[10px] tracking-widest uppercase text-neutral-500 mt-0.5">
                        {(tpl.blocks || []).length} bloques · {totalEx} ejercicios
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteTemplate(tpl.id); }}
                      className="text-neutral-500 hover:text-red-500 p-1"
                      data-testid={`delete-day-template-${tpl.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!day.rest && day.blocks.map((b, bi) => (
        <div key={b.id} className="border border-white/10 bg-[#0a0a0a] p-3 space-y-3">
          <div className="flex items-center gap-2">
            <select
              value={b.type}
              onChange={(e) => updateBlock(bi, { type: e.target.value })}
              className="qv-input max-w-[180px]"
            >
              {BLOCK_TYPE_LIST.map((bt) => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
            </select>
            <input
              value={b.name || ""}
              onChange={(e) => updateBlock(bi, { name: e.target.value })}
              placeholder="Nombre bloque (opcional)"
              className="qv-input flex-1"
            />
            <button type="button" onClick={() => delBlock(bi)} className="text-neutral-500 hover:text-red-500 p-2" aria-label="Eliminar bloque">
              <Trash2 size={14} />
            </button>
          </div>
          {b.exercises.map((ex, ei) => (
            <div key={ex.id} className="border-t border-white/5 pt-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                <div className="sm:col-span-6">
                  <ExerciseNameInput
                    value={ex.name}
                    onChange={(v) => updateEx(bi, ei, { name: v })}
                    onPick={(sug) => updateEx(bi, ei, {
                      name: sug.name || ex.name,
                      // Only overwrite empty fields so coach's adjustments are preserved
                      youtube_url: ex.youtube_url || sug.youtube_url || "",
                      sets: ex.sets || sug.sets || "",
                      reps: ex.reps || sug.reps || "",
                      intensity: ex.intensity || sug.intensity || "",
                      load: ex.load || sug.load || "",
                      tempo: ex.tempo || sug.tempo || "",
                      rest: ex.rest || sug.rest || "",
                    })}
                  />
                </div>
                <input value={ex.sets} onChange={(e) => updateEx(bi, ei, { sets: e.target.value })} placeholder="Series" className="qv-input" />
                <input value={ex.reps} onChange={(e) => updateEx(bi, ei, { reps: e.target.value })} placeholder="Reps" className="qv-input" />
                <input value={ex.rest} onChange={(e) => updateEx(bi, ei, { rest: e.target.value })} placeholder="Descanso" className="qv-input" />
                <input value={ex.load} onChange={(e) => updateEx(bi, ei, { load: e.target.value })} placeholder="Carga" className="qv-input" />
                <input value={ex.tempo} onChange={(e) => updateEx(bi, ei, { tempo: e.target.value })} placeholder="Tempo" className="qv-input" />
                <input value={ex.intensity} onChange={(e) => updateEx(bi, ei, { intensity: e.target.value })} placeholder="Intensidad" className="qv-input" />
              </div>
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Youtube size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                  <input value={ex.youtube_url} onChange={(e) => updateEx(bi, ei, { youtube_url: e.target.value })} placeholder="URL YouTube" className="qv-input pl-9" />
                </div>
                <input value={ex.notes} onChange={(e) => updateEx(bi, ei, { notes: e.target.value })} placeholder="Notas/técnica" className="qv-input flex-1" />
                <button type="button" onClick={() => delEx(bi, ei)} className="text-neutral-500 hover:text-red-500 p-2" aria-label="Eliminar ejercicio">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => addEx(bi)} className="qv-btn-ghost text-xs">
            <Plus size={12} /> AÑADIR EJERCICIO
          </button>
        </div>
      ))}

      {!day.rest && (
        <button type="button" onClick={addBlock} className="qv-btn-ghost text-xs">
          <Plus size={12} /> AÑADIR BLOQUE
        </button>
      )}

      {!day.rest && (
        <textarea
          value={day.notes || ""}
          onChange={(e) => update({ notes: e.target.value })}
          placeholder="Notas para el atleta (opcional)"
          rows={2}
          className="qv-input"
          style={{ height: "auto", padding: "12px 16px" }}
        />
      )}
    </div>
  );
};

/* ---------- Main editor ---------- */
const ListField = ({ label, values, onChange, placeholder }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">{label}</label>
    <div className="space-y-2">
      {(values || []).map((v, i) => (
        <div key={i} className="flex gap-2">
          <input value={v} onChange={(e) => { const c = (values || []).slice(); c[i] = e.target.value; onChange(c); }} placeholder={placeholder} className="qv-input flex-1" />
          <button type="button" onClick={() => onChange((values || []).filter((_, j) => j !== i))} className="text-neutral-500 hover:text-red-500 p-2" aria-label="Eliminar">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </div>
    <button type="button" onClick={() => onChange([...(values || []), ""])} className="qv-btn-ghost text-xs mt-2">
      <Plus size={12} /> AÑADIR
    </button>
  </div>
);

export default function AdminProgramEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("info");
  const [openWeek, setOpenWeek] = useState(0);
  const [dayTemplates, setDayTemplates] = useState([]);

  const loadDayTemplates = async () => {
    try {
      const { data } = await api.get("/admin/session-templates");
      setDayTemplates(data || []);
    } catch { /* noop */ }
  };

  const saveDayTemplate = async (payload) => {
    try {
      const { data: created } = await api.post("/admin/session-templates", payload);
      setDayTemplates((arr) => [created, ...arr]);
      toast.success("Plantilla guardada");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const deleteDayTemplate = async (tid) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;
    try {
      await api.delete(`/admin/session-templates/${tid}`);
      setDayTemplates((arr) => arr.filter((t) => t.id !== tid));
      toast.success("Plantilla eliminada");
    } catch (err) { toast.error(formatErr(err)); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/programs/${id}`);
        if (alive) setProgram({
          ...data,
          outcomes: data.outcomes || [],
          for_who: data.for_who || [],
          not_for: data.not_for || [],
          faq: data.faq || [],
          testimonials: data.testimonials || [],
          weeks: (data.weeks && data.weeks.length ? data.weeks : [emptyWeek(1)]),
        });
      } catch (err) {
        toast.error(formatErr(err));
      }
    })();
    loadDayTemplates();
    return () => { alive = false; };
  }, [id]);

  if (!program) {
    return <AppShell><div className="p-8 text-neutral-500">Cargando...</div></AppShell>;
  }

  const setP = (patch) => setProgram({ ...program, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        slug: program.slug,
        title: program.title,
        subtitle: program.subtitle,
        price_eur: parseFloat(program.price_eur) || 0,
        duration_weeks: parseInt(program.duration_weeks, 10) || 1,
        level: program.level,
        target_audience: program.target_audience,
        cover_image: program.cover_image,
        hero_tagline: program.hero_tagline,
        description: program.description,
        outcomes: program.outcomes,
        for_who: program.for_who,
        not_for: program.not_for,
        faq: program.faq,
        testimonials: program.testimonials,
        preview_week_index: parseInt(program.preview_week_index, 10) || 0,
        preview_day_index: parseInt(program.preview_day_index, 10) || 0,
        weeks: program.weeks,
        is_published: program.is_published,
      };
      const { data } = await api.put(`/admin/programs/${id}`, payload);
      setProgram({ ...data,
        outcomes: data.outcomes || [], for_who: data.for_who || [], not_for: data.not_for || [],
        faq: data.faq || [], testimonials: data.testimonials || [],
        weeks: data.weeks && data.weeks.length ? data.weeks : program.weeks,
      });
      toast.success("Guardado.");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const addWeek = () => setP({ weeks: [...program.weeks, emptyWeek((program.weeks?.length || 0) + 1)] });
  const delWeek = (wi) => {
    if (!window.confirm("Eliminar esta semana?")) return;
    setP({ weeks: program.weeks.filter((_, i) => i !== wi) });
  };
  const updateWeek = (wi, patch) => {
    const weeks = program.weeks.slice();
    weeks[wi] = { ...weeks[wi], ...patch };
    setP({ weeks });
  };
  const addDay = (wi) => updateWeek(wi, { days: [...program.weeks[wi].days, emptyDay("Lunes")] });
  const updateDay = (wi, di, day) => {
    const days = program.weeks[wi].days.slice();
    days[di] = day;
    updateWeek(wi, { days });
  };
  const delDay = (wi, di) => updateWeek(wi, { days: program.weeks[wi].days.filter((_, i) => i !== di) });

  /* helpers for list-input fields */
  const setListValue = (field, values) => setP({ [field]: values });

  return (
    <AppShell>
      <div className="p-5 sm:p-8" data-testid="admin-program-editor">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <Link to="/app/admin/programas" className="text-[11px] tracking-[0.22em] uppercase text-neutral-300 hover:text-white inline-flex items-center gap-1.5">
            <ArrowLeft size={12} /> Volver a programas
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setP({ is_published: !program.is_published })} className="qv-btn-ghost text-xs" data-testid="editor-toggle-publish">
              {program.is_published ? <><EyeOff size={13} /> BORRADOR</> : <><Eye size={13} /> PUBLICAR</>}
            </button>
            <button type="button" onClick={save} disabled={saving} className="qv-btn-primary text-xs disabled:opacity-60" data-testid="editor-save">
              <Save size={14} /> {saving ? "GUARDANDO..." : "GUARDAR"}
            </button>
          </div>
        </div>

        <div className="qv-overline">{program.is_published ? "PUBLICADO" : "BORRADOR"}</div>
        <h1 className="qv-display text-white text-3xl sm:text-4xl mt-2">{program.title}</h1>

        {/* Tabs */}
        <div className="mt-8 border-b border-white/10 flex flex-wrap gap-4">
          {[
            { id: "info", label: "Información" },
            { id: "marketing", label: "Marketing" },
            { id: "content", label: "Contenido" },
            { id: "extras", label: "FAQ & Testimonios" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`pb-3 text-xs tracking-[0.22em] uppercase font-bold ${tab === t.id ? "text-white border-b-2 border-[color:var(--qv-red)]" : "text-neutral-500 hover:text-white"}`}
              data-testid={`editor-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-8 max-w-4xl">
          {tab === "info" && (
            <div className="space-y-5" data-testid="tab-info">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Título*</label>
                  <input value={program.title || ""} onChange={(e) => setP({ title: e.target.value })} className="qv-input" data-testid="editor-input-title" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Slug URL</label>
                  <input value={program.slug || ""} onChange={(e) => setP({ slug: e.target.value })} className="qv-input" data-testid="editor-input-slug" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Subtítulo</label>
                  <input value={program.subtitle || ""} onChange={(e) => setP({ subtitle: e.target.value })} className="qv-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Nivel</label>
                  <select value={program.level || ""} onChange={(e) => setP({ level: e.target.value })} className="qv-input">
                    <option value="">—</option>
                    <option value="Iniciación">Iniciación</option>
                    <option value="Intermedio">Intermedio</option>
                    <option value="Avanzado">Avanzado</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Precio (€)*</label>
                  <input type="number" step="1" value={program.price_eur ?? 0} onChange={(e) => setP({ price_eur: e.target.value })} className="qv-input" data-testid="editor-input-price" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Duración (semanas)*</label>
                  <input type="number" min={1} value={program.duration_weeks || 1} onChange={(e) => setP({ duration_weeks: e.target.value })} className="qv-input" data-testid="editor-input-duration" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Imagen de portada</label>
                <ImageInput
                  value={program.cover_image || ""}
                  onChange={(v) => setP({ cover_image: v })}
                  testId="cover-image"
                  label="portada"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Público objetivo</label>
                <input value={program.target_audience || ""} onChange={(e) => setP({ target_audience: e.target.value })} placeholder="Ej. Futbolistas amateur en pretemporada" className="qv-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Tagline del hero</label>
                <input value={program.hero_tagline || ""} onChange={(e) => setP({ hero_tagline: e.target.value })} placeholder="Una frase que enganche" className="qv-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-neutral-400 block mb-2">Descripción larga</label>
                <textarea value={program.description || ""} onChange={(e) => setP({ description: e.target.value })} rows={5} className="qv-input" style={{ height: "auto", padding: "12px 16px" }} />
              </div>
            </div>
          )}

          {tab === "marketing" && (
            <div className="space-y-8" data-testid="tab-marketing">
              <ListField label="Qué vas a conseguir" values={program.outcomes} onChange={(v) => setListValue("outcomes", v)} placeholder="Ej. + Fuerza en tren inferior" />
              <ListField label="Para ti si..." values={program.for_who} onChange={(v) => setListValue("for_who", v)} placeholder="Ej. Juegas federado" />
              <ListField label="No es para ti si..." values={program.not_for} onChange={(v) => setListValue("not_for", v)} placeholder="Ej. Buscas plan personalizado" />
            </div>
          )}

          {tab === "content" && (
            <div className="space-y-5" data-testid="tab-content">
              <div className="border border-white/10 bg-[#0a0a0a] p-4 flex flex-wrap gap-3 items-center">
                <Star size={14} color="var(--qv-red)" />
                <span className="text-xs uppercase tracking-[0.2em] text-neutral-400">Sesión Preview (visible sin comprar)</span>
                <input type="number" min={0} value={program.preview_week_index ?? 0} onChange={(e) => setP({ preview_week_index: e.target.value })} className="qv-input max-w-[80px]" title="Índice semana" />
                <span className="text-neutral-500 text-xs">semana</span>
                <input type="number" min={0} value={program.preview_day_index ?? 0} onChange={(e) => setP({ preview_day_index: e.target.value })} className="qv-input max-w-[80px]" title="Índice día" />
                <span className="text-neutral-500 text-xs">día</span>
              </div>
              {program.weeks.map((w, wi) => (
                <div key={w.id} className="border border-white/10 bg-[#050505]" data-testid={`editor-week-${wi}`}>
                  <div className="flex items-center gap-2 p-4 border-b border-white/5">
                    <button type="button" onClick={() => setOpenWeek(openWeek === wi ? -1 : wi)} className="text-neutral-400 hover:text-white p-1">
                      {openWeek === wi ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <input type="number" min={1} value={w.week_number} onChange={(e) => updateWeek(wi, { week_number: parseInt(e.target.value, 10) || 1 })} className="qv-input max-w-[80px]" />
                    <input value={w.title || ""} onChange={(e) => updateWeek(wi, { title: e.target.value })} placeholder="Título semana" className="qv-input flex-1" />
                    <button type="button" onClick={() => delWeek(wi)} className="text-neutral-500 hover:text-red-500 p-2" aria-label="Eliminar semana">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {openWeek === wi && (
                    <div className="p-4 space-y-3">
                      <input value={w.subtitle || ""} onChange={(e) => updateWeek(wi, { subtitle: e.target.value })} placeholder="Subtítulo semana (opcional)" className="qv-input" />
                      {w.days.map((d, di) => (
                        <DayEditor
                          key={d.id}
                          day={d}
                          onChange={(newDay) => updateDay(wi, di, newDay)}
                          onDelete={() => delDay(wi, di)}
                          templates={dayTemplates}
                          onSaveTemplate={saveDayTemplate}
                          onDeleteTemplate={deleteDayTemplate}
                        />
                      ))}
                      <button type="button" onClick={() => addDay(wi)} className="qv-btn-ghost text-xs">
                        <Plus size={12} /> AÑADIR DÍA
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <button type="button" onClick={addWeek} className="qv-btn-ghost">
                <Plus size={14} /> AÑADIR SEMANA
              </button>
            </div>
          )}

          {tab === "extras" && (
            <div className="space-y-8" data-testid="tab-extras">
              {/* FAQ */}
              <div>
                <div className="qv-overline mb-3">Preguntas frecuentes</div>
                <div className="space-y-3">
                  {(program.faq || []).map((f, i) => (
                    <div key={i} className="border border-white/10 bg-[#0a0a0a] p-4 space-y-2">
                      <input value={f.q} onChange={(e) => { const c = program.faq.slice(); c[i] = { ...c[i], q: e.target.value }; setP({ faq: c }); }} placeholder="Pregunta" className="qv-input" />
                      <textarea value={f.a} onChange={(e) => { const c = program.faq.slice(); c[i] = { ...c[i], a: e.target.value }; setP({ faq: c }); }} placeholder="Respuesta" rows={3} className="qv-input" style={{ height: "auto", padding: "12px 16px" }} />
                      <button type="button" onClick={() => setP({ faq: program.faq.filter((_, j) => j !== i) })} className="text-neutral-500 hover:text-red-500 p-1 text-xs inline-flex items-center gap-1">
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setP({ faq: [...(program.faq || []), { q: "", a: "" }] })} className="qv-btn-ghost text-xs mt-3">
                  <Plus size={12} /> AÑADIR PREGUNTA
                </button>
              </div>

              {/* Testimonials */}
              <div>
                <div className="qv-overline mb-3">Testimonios del programa</div>
                <div className="space-y-3">
                  {(program.testimonials || []).map((t, i) => (
                    <div key={i} className="border border-white/10 bg-[#0a0a0a] p-4 space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input value={t.name} onChange={(e) => { const c = program.testimonials.slice(); c[i] = { ...c[i], name: e.target.value }; setP({ testimonials: c }); }} placeholder="Nombre" className="qv-input" />
                        <input value={t.role || ""} onChange={(e) => { const c = program.testimonials.slice(); c[i] = { ...c[i], role: e.target.value }; setP({ testimonials: c }); }} placeholder="Club / Rol" className="qv-input" />
                      </div>
                      <textarea value={t.quote} onChange={(e) => { const c = program.testimonials.slice(); c[i] = { ...c[i], quote: e.target.value }; setP({ testimonials: c }); }} placeholder="Testimonio" rows={2} className="qv-input" style={{ height: "auto", padding: "12px 16px" }} />
                      <div>
                        <label className="text-[10px] tracking-widest uppercase text-neutral-500 block mb-1">Foto (opcional)</label>
                        <ImageInput
                          value={t.photo_url || ""}
                          onChange={(v) => { const c = program.testimonials.slice(); c[i] = { ...c[i], photo_url: v }; setP({ testimonials: c }); }}
                          testId={`testimonial-${i}-photo`}
                          label={`testimonio ${t.name || i + 1}`}
                        />
                      </div>
                      <button type="button" onClick={() => setP({ testimonials: program.testimonials.filter((_, j) => j !== i) })} className="text-neutral-500 hover:text-red-500 p-1 text-xs inline-flex items-center gap-1">
                        <Trash2 size={12} /> Eliminar
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setP({ testimonials: [...(program.testimonials || []), { name: "", role: "", quote: "", photo_url: "" }] })} className="qv-btn-ghost text-xs mt-3">
                  <Plus size={12} /> AÑADIR TESTIMONIO
                </button>
              </div>

              {/* Buyers */}
              <Buyers programId={program.id} />
            </div>
          )}
        </div>

        {/* Sticky save at bottom of mobile */}
        <div className="mt-10">
          <button type="button" onClick={save} disabled={saving} className="qv-btn-primary w-full sm:w-auto disabled:opacity-60">
            <Save size={16} /> {saving ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
          </button>
        </div>
      </div>
    </AppShell>
  );
}

const Buyers = ({ programId }) => {
  const [buyers, setBuyers] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/admin/programs/${programId}/purchases`);
        if (alive) setBuyers(data);
      } catch { /* no-op */ }
    })();
    return () => { alive = false; };
  }, [programId]);
  return (
    <div>
      <div className="qv-overline mb-3 flex items-center gap-2"><Users size={13} /> Compradores</div>
      {buyers.length === 0 ? (
        <div className="text-neutral-500 text-sm">Todavía nadie ha comprado este programa.</div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5">
          {buyers.map((b) => (
            <div key={b.id} className="p-3 flex items-center justify-between gap-3 text-sm">
              <div>
                <div className="text-white">{b.email}</div>
                <div className="text-[11px] text-neutral-500">{b.status} · {b.paid_at || b.created_at}</div>
              </div>
              <div className="text-neutral-400 text-xs">{Math.round(b.amount || 0)}€</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
